import { AbstractControl, ValidatorFn, FormGroup } from '@angular/forms';
import * as moment from 'moment';

//from api :  1 => 'visa', 2 => 'euromastercard', 3 => 'americanexpress',
const _TYPE = {
    Mastercard: ["mastercard", "euromastercard"],
    Visa: ["visa"],
    Amex: ["amex", "americanexpress"],
    Discover: ["discover"],
    UATP: ["uatp"],
    BC: ["bc"],
    Dankort: ["dankort"],
    DinersClub: ["dinersclub"],
    Electron: ["electron"],
    Elo: ["elo"],
    Hipercard: ["hipercard"],
    JCB: ["jcb"],
    Maestro: ["maestro"],
    UnionPay: ["unionpay"],
    Unknown: ["unknown"]
};

export const numeroValidator: ValidatorFn = (fg: FormGroup) => {
    if (!fg.parent) {
        return null;
    }
    const numero = "" + (fg.value ? fg.value : '');
    const typeCart = fg.parent.get('type').value;
    const res: boolean = cardValidation(numero, typeCart);
    return !res ? { "numerocart": true } : null;
}
export const cryptoGrammeValidator: ValidatorFn = (fg: FormGroup) => {
    if (!fg.parent) {
        return null;
    }
    const typeCart: string = fg.parent.get('type').value.toString();
    const val = (fg.value || '').toString();
    if (!val) {
        return { "cryptogramme": { requiredLength: 3 } }
    }
    const expectedLength: number = ccvCount(val, typeCart);
    const isValid: boolean = cvvValidation(val, typeCart);
    if (!isValid) {
        return { "cryptogramme": { requiredLength: expectedLength } }
    }
    return null;
}
export const monthValidator: ValidatorFn = (fg: FormGroup) => {
    if (!fg.parent) {
        return null;
    }
    const month = parseInt(fg.parent.get('month').value);
    const year = parseInt(fg.parent.get('year').value);
    const currentYear = parseInt(moment().format('YYYY'));
    const currentMonth = parseInt(moment().format('MM'));
    if (currentYear == year && currentMonth > month) {
        return { "month": true }
    }
    return null;
}

// Algorithme de Luhn corrigé
function luhnCheck(cardNumber: string): boolean {
    let sum = 0;
    let alternate = false;

    // Parcourir de droite à gauche
    for (let i = cardNumber.length - 1; i >= 0; i--) {
        let digit = parseInt(cardNumber.charAt(i));

        if (alternate) {
            digit *= 2;
            // Si le résultat est > 9, soustraire 9 (équivalent à additionner les chiffres)
            if (digit > 9) {
                digit -= 9;
            }
        }

        sum += digit;
        alternate = !alternate;
    }

    return (sum % 10) === 0;
}

// Fonction de validation de carte corrigée
function cardValidation(cardNumber: string, typeCart: string): boolean {
    // Nettoyer le numéro (enlever espaces et tirets)
    const cleanNumber = cardNumber.replace(/[\s-]/g, '');

    // Vérifier que ce sont tous des chiffres
    if (!/^\d+$/.test(cleanNumber)) {
        return false;
    }

    // Vérifier l'algorithme de Luhn ET le type de carte
    const detectedType = detectCardType(cleanNumber);
    const length = cleanNumber.length;
    const validLength = detectedType === _TYPE.Visa ? [13, 16, 19].indexOf(length) !== -1 :
        detectedType === _TYPE.Amex || detectedType === _TYPE.UATP ? length === 15 :
        detectedType === _TYPE.DinersClub ? length === 14 :
        detectedType === _TYPE.Maestro ? length >= 12 && length <= 19 :
        detectedType === _TYPE.UnionPay ? length >= 16 && length <= 19 : length === 16;
    return validLength && luhnCheck(cleanNumber) && detectedType.indexOf(typeCart) !== -1;
}

// Identify a brand as soon as its identifying prefix is available.
export function detectCardType(cardNumber: string): string[] {
    const number = (cardNumber || '').replace(/\D/g, '');
    if (/^40117[89]|^438935|^45763[12]|^431274|^451416|^457393|^504175/.test(number)) return _TYPE.Elo;
    if (/^4026|^417500|^4405|^4508|^4844|^4913|^4917/.test(number)) return _TYPE.Electron;
    if (/^5019/.test(number)) return _TYPE.Dankort;
    if (/^5018|^5020|^5038|^5893|^6304|^6759|^676[1-3]/.test(number)) return _TYPE.Maestro;
    if (/^3841(00|40|60)|^606282/.test(number)) return _TYPE.Hipercard;
    if (/^3[47]/.test(number)) return _TYPE.Amex;
    if (/^5[1-5]/.test(number) || /^2(2[2-9]|[3-6]|7[01]|720)/.test(number)) return _TYPE.Mastercard;
    if (/^6011/.test(number) || /^65/.test(number) || /^64[4-9]/.test(number) || /^622(1(2[6-9]|[3-9])|[2-8]|9([01]|2[0-5]))/.test(number)) return _TYPE.Discover;
    if (/^35/.test(number)) return _TYPE.JCB;
    if (/^30[0-5]/.test(number) || /^3[68]/.test(number)) return _TYPE.DinersClub;
    if (/^62/.test(number)) return _TYPE.UnionPay;
    if (/^1/.test(number)) return _TYPE.UATP;
    if (/^4/.test(number)) return _TYPE.Visa;
    return _TYPE.Unknown;
}

// Validation du CVV selon le type de carte choisi
function cvvValidation(cvv: string, chosenType: string | string[]): boolean {
    const cvvDigits = cvv.replace(/\s/g, '');
    if (!/^\d+$/.test(cvvDigits)) return false;

    const expectedLength = ccvCount(cvv, chosenType);

    return cvvDigits.length === expectedLength;
}

function ccvCount(cvv: string, chosenType: string | string[]): number {
    // Normaliser le type (tableau d'alias)
    const aliases = Array.isArray(chosenType) ? chosenType : [chosenType];
    const lowerAliases = aliases.map(a => a.toLowerCase());

    // Utiliser la table _TYPE pour trouver les alias Amex (plus robuste)
    const amexAliases = _TYPE.Amex.map(a => a.toLowerCase());
    const isAmex = lowerAliases.some(a => /*amexAliases.includes(a)*/ amexAliases.indexOf(a) !== -1);

    // Règle générale: Amex => 4, sinon 3
    const expectedLength = isAmex ? 4 : 3;
    return expectedLength;
}
